import React, { useState, useEffect } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AssignmentTable from '../components/AssignmentTable';
import FinalExamCalculator from '../components/FinalExamCalculator';
import { PlusIcon, PencilIcon, TrashIcon } from 'lucide-react';

export default function Dashboard({ user, onLogout }) {
  const [classes, setClasses] = useState([]);
  const [activeClassId, setActiveClassId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newClassName, setNewClassName] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [editClassName, setEditClassName] = useState('');

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const data = await api.getClasses();
      setClasses(data);
      if (data.length > 0 && !activeClassId) {
        setActiveClassId(data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClass = async (e) => {
    e.preventDefault();
    if (!newClassName.trim()) return;

    try {
      const newClass = await api.createClass(newClassName.trim());
      setClasses([newClass, ...classes]);
      setActiveClassId(newClass.id);
      setNewClassName('');
      setAddDialogOpen(false);
      toast.success('Class added successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to add class');
    }
  };

  const handleEditClass = async (e) => {
    e.preventDefault();
    if (!editClassName.trim() || !editingClass) return;

    try {
      await api.updateClass(editingClass.id, editClassName.trim());
      setClasses(classes.map(c => c.id === editingClass.id ? { ...c, className: editClassName.trim() } : c));
      setEditDialogOpen(false);
      setEditingClass(null);
      setEditClassName('');
      toast.success('Class updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update class');
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class? All assignments will also be deleted.')) {
      return;
    }

    try {
      await api.deleteClass(classId);
      const newClasses = classes.filter(c => c.id !== classId);
      setClasses(newClasses);
      if (activeClassId === classId) {
        setActiveClassId(newClasses.length > 0 ? newClasses[0].id : null);
      }
      toast.success('Class deleted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to delete class');
    }
  };

  const activeClass = classes.find(c => c.id === activeClassId);

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))]">
      {/* Navbar */}
      <header className="sticky top-0 z-40 border-b bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70" data-testid="navbar">
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-[Space_Grotesk] text-lg font-semibold tracking-tight text-[hsl(var(--fg))]">Grade Percentile Tracker</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-muted-foreground" data-testid="user-email">{user?.email}</span>
            <Button variant="secondary" size="sm" onClick={onLogout} data-testid="logout-button">Logout</Button>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-lg">Loading...</div>
        </div>
      ) : (
        <main className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8 py-6">
          {classes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Card className="w-full max-w-md text-center">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold mb-2">No classes yet</h3>
                  <p className="text-muted-foreground mb-4">Get started by adding your first class</p>
                  <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-first-class-button">
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Add Your First Class
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Class</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddClass} className="space-y-4">
                        <Input
                          placeholder="Class name (e.g., Math 101)"
                          value={newClassName}
                          onChange={(e) => setNewClassName(e.target.value)}
                          data-testid="class-name-input"
                        />
                        <Button type="submit" className="w-full" data-testid="add-class-submit-button">
                          Add Class
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {/* Sidebar */}
              <aside className="hidden md:block md:col-span-3">
                <div className="sticky top-20">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Classes</h2>
                    <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="ghost" data-testid="add-class-button">
                          <PlusIcon className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add New Class</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddClass} className="space-y-4">
                          <Input
                            placeholder="Class name (e.g., Math 101)"
                            value={newClassName}
                            onChange={(e) => setNewClassName(e.target.value)}
                            data-testid="class-name-input"
                          />
                          <Button type="submit" className="w-full" data-testid="add-class-submit-button">
                            Add Class
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="space-y-2">
                    {classes.map((cls) => (
                      <div
                        key={cls.id}
                        className={`group relative p-3 rounded-md border bg-white hover:bg-blue-50 transition-colors cursor-pointer ${
                          activeClassId === cls.id ? 'border-blue-600 bg-blue-50' : ''
                        }`}
                        onClick={() => setActiveClassId(cls.id)}
                        data-testid="class-list-item"
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate font-medium text-sm">{cls.className}</span>
                          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[hsl(var(--secondary))] font-semibold">
                            {cls.currentPercent !== null && cls.currentPercent !== undefined ? `${cls.currentPercent}%` : 'N/A'}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {cls.totalPossible > 0 ? `${cls.totalEarned} / ${cls.totalPossible} pts` : 'No grades yet'}
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingClass(cls);
                              setEditClassName(cls.className);
                              setEditDialogOpen(true);
                            }}
                            className="p-1 hover:bg-blue-100 rounded"
                            data-testid="edit-class-button"
                          >
                            <PencilIcon className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClass(cls.id);
                            }}
                            className="p-1 hover:bg-red-100 rounded"
                            data-testid="delete-class-button"
                          >
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </aside>

              {/* Mobile tabs */}
              <div className="md:hidden">
                <Tabs value={activeClassId} onValueChange={setActiveClassId}>
                  <TabsList className="w-full overflow-x-auto no-scrollbar flex justify-start">
                    {classes.map((cls) => (
                      <TabsTrigger key={cls.id} value={cls.id} data-testid="class-tab" className="whitespace-nowrap">
                        {cls.className}
                      </TabsTrigger>
                    ))}
                    <TabsTrigger value="add" asChild>
                      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                        <DialogTrigger asChild>
                          <button className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium" data-testid="add-class-mobile-button">
                            <PlusIcon className="w-4 h-4" />
                          </button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Add New Class</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleAddClass} className="space-y-4">
                            <Input
                              placeholder="Class name (e.g., Math 101)"
                              value={newClassName}
                              onChange={(e) => setNewClassName(e.target.value)}
                              data-testid="class-name-input"
                            />
                            <Button type="submit" className="w-full" data-testid="add-class-submit-button">
                              Add Class
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Main content */}
              <section className="md:col-span-9 space-y-6">
                {activeClass && (
                  <>
                    {/* Grade summary card */}
                    <Card className="relative overflow-hidden">
                      <CardHeader>
                        <CardTitle className="font-[Space_Grotesk] text-3xl sm:text-4xl" data-testid="current-grade-percent">
                          {activeClass.currentPercent !== null && activeClass.currentPercent !== undefined ? `${activeClass.currentPercent}%` : 'No grades yet'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-muted-foreground">
                        <div className="text-sm" data-testid="points-summary">
                          {activeClass.totalPossible > 0 ? `${activeClass.totalEarned} / ${activeClass.totalPossible} points` : 'Add assignments to see your grade'}
                        </div>
                      </CardContent>
                      <span aria-hidden className="pointer-events-none absolute -right-10 -top-10 size-40 rounded-full bg-blue-50"></span>
                    </Card>

                    {/* Assignments */}
                    <AssignmentTable classId={activeClassId} onUpdate={loadClasses} />

                    {/* Final exam calculator */}
                    {activeClass.currentPercent !== null && activeClass.currentPercent !== undefined && (
                      <FinalExamCalculator currentPercent={activeClass.currentPercent || 0} />
                    )}
                  </>
                )}
              </section>
            </div>
          )}
        </main>
      )}

      {/* Edit class dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Class</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditClass} className="space-y-4">
            <Input
              placeholder="Class name"
              value={editClassName}
              onChange={(e) => setEditClassName(e.target.value)}
              data-testid="edit-class-name-input"
            />
            <Button type="submit" className="w-full" data-testid="edit-class-submit-button">
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}